import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { createInitialState, gameReducer } from './reducer';
import { loadGame, rememberOutcome, saveGame } from './storage';
import type { GameAction, GameState } from './types';

interface GameContextValue {
  state: GameState;
  dispatch: Dispatch<GameAction>;
}

const GameContext = createContext<GameContextValue | null>(null);

function initialFromStorage(): GameState {
  return loadGame() ?? createInitialState();
}

export function GameProvider({ children }: PropsWithChildren) {
  const [state, dispatch] = useReducer(gameReducer, undefined, initialFromStorage);
  const cinematic = state.phase === 'reveal' || Boolean(state.courtEvent);

  useEffect(() => {
    saveGame(state);
  }, [state]);

  useEffect(() => {
    if (state.phase === 'aftermath' && state.outcome) rememberOutcome(state.outcome);
  }, [state.phase, state.outcome]);

  useEffect(() => {
    const interval = window.setInterval(
      () => dispatch({ type: 'TICK', ms: cinematic ? 100 : 1000 }),
      cinematic ? 100 : 1000,
    );
    return () => window.clearInterval(interval);
  }, [cinematic]);

  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame(): GameContextValue {
  const context = useContext(GameContext);
  if (!context) throw new Error('useGame must be used inside GameProvider');
  return context;
}
